import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowRight, FileText } from 'lucide-react';
import { api, WORKER_API_BASE, iframeClientParam } from '../lib/api';
import { tr } from '../lib/i18nHelpers';

interface PendingMismatch {
  id: string;
  original_name?: string | null;
  filename?: string | null;
  ocr_channel?: string | null;
  ocr_detected_type?: string | null;
}

const LABELS: Record<string, [string, string, string]> = {
  bank_statement: ['Bank Statement', '銀行月結單', '银行月结单'],
  card_statement: ['Card Statement', '信用卡月結單', '信用卡月结单'],
  sales_invoice: ['Sales Invoice', '銷項發票', '销项发票'],
  purchase_invoice: ['Purchase Invoice', '購項發票', '购项发票'],
  invoice: ['Invoice', '發票', '发票'],
  statement: ['Bank Statement', '銀行月結單', '银行月结单'],
  mixed: ['Mixed documents', '混合文件', '混合文件'],
  unknown: ['Unknown', '未知', '未知'],
};

function label(key: string | null | undefined): string {
  const [en, zh, cn] = LABELS[key || ''] || [key || 'Unknown', key || '未知', key || '未知'];
  return tr(en, zh, cn);
}

/** The dialog can only "switch" when the detected class maps to an importer. */
function canSwitch(detected: string): boolean {
  return ['statement', 'bank_statement', 'card_statement', 'invoice', 'sales_invoice', 'purchase_invoice', 'receipt']
    .includes(detected);
}

/**
 * Global watcher for background OCR type mismatches.
 *
 * Multi-page uploads are OCR'd in the background, so the mismatch cannot use
 * the inline dialog from FileUpload. This polls the same server-side list and
 * shows the same switch / force / cancel choices on any page.
 */
export default function TypeMismatchWatcher() {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<PendingMismatch[]>([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api('/file-storage/type-mismatches') as { data?: PendingMismatch[] };
      setPending(res?.data || []);
    } catch { /* transient */ }
  }, []);

  useEffect(() => {
    void load();
    const timer = setInterval(() => { void load(); }, 8000);
    return () => clearInterval(timer);
  }, [load]);

  const item = pending[0];
  if (!item) return null;

  const detected = item.ocr_detected_type || 'unknown';
  const mixed = detected === 'mixed';
  const switchable = canSwitch(detected);
  const name = item.original_name || item.filename || '';

  const resolve = async (action: 'switch' | 'force' | 'cancel') => {
    setBusy(true);
    try {
      await api(`/file-storage/${item.id}/resolve-type-mismatch`, {
        method: 'POST',
        body: { action },
      });
      // Refresh every list/record view with the resolved (or removed) file.
      // 2026-11-03: was window.location.reload() — cache invalidation refreshes
      // the live lists without unmounting the app (no full-page flash).
      await queryClient.invalidateQueries();
      await load();
    } catch {
      setBusy(false);
      void load();
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      {/* 2026-11-03: two-pane dialog — choices on the left, a PDF preview panel
          slides open on the right so the user can judge the detected type
          before switching/forcing. Stacks under the choices on small screens. */}
      <div className="bg-card border rounded-xl shadow-xl w-full max-w-5xl flex flex-col sm:flex-row max-h-[85vh] overflow-hidden">
        <div className="flex-1 min-w-[320px] p-6 space-y-4 overflow-y-auto">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-6 w-6 text-amber-900 dark:text-amber-100 shrink-0 mt-0.5" />
            <div>
              <h3 className="text-lg font-bold">
                {tr('Document Type Mismatch', '文件類型不符', '文件类型不符')}
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                {mixed
                  ? tr(
                      `"${name}" contains mixed document types. It was not imported. Upload mixed batches from the Collections tab, or force it as "${label(item.ocr_channel)}".`,
                      `「${name}」包含多種文件類型，未進行匯入。請從「文件批次」頁面上傳混合文件，或強制作為「${label(item.ocr_channel)}」。`,
                      `「${name}」包含多种文件类型，未进行导入。请从「文件批次」页面上传混合文件，或强制作为「${label(item.ocr_channel)}」。`,
                    )
                  : tr(
                      `"${name}" was uploaded as "${label(item.ocr_channel)}" but OCR detected it as "${label(detected)}".`,
                      `「${name}」上傳為「${label(item.ocr_channel)}」，但 OCR 檢測為「${label(detected)}」。`,
                      `「${name}」上传为「${label(item.ocr_channel)}」，但 OCR 检测为「${label(detected)}」。`,
                    )}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 justify-end pt-2">
            <button
              disabled={busy}
              onClick={() => resolve('cancel')}
              className="px-3 py-2 border rounded-lg text-sm hover:bg-muted disabled:opacity-50"
            >
              {tr('Cancel', '取消', '取消')}
            </button>
            <button
              disabled={busy}
              onClick={() => resolve('force')}
              className="px-4 py-2 bg-amber-900 text-white rounded-lg text-sm font-medium hover:bg-amber-800 disabled:opacity-50"
            >
              {tr(`Force as ${label(item.ocr_channel)}`, `強制作為${label(item.ocr_channel)}`, `强制作为${label(item.ocr_channel)}`)}
            </button>
            {switchable && (
              <button
                disabled={busy}
                onClick={() => resolve('switch')}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5"
              >
                {tr(`Switch to ${label(detected)}`, `切換至${label(detected)}`, `切换至${label(detected)}`)}
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Right: PDF preview */}
        <div className="sm:w-1/2 h-72 sm:h-auto flex flex-col border-t sm:border-t-0 sm:border-l bg-muted/30">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b shrink-0">
            <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="text-sm font-medium truncate">{name || tr('Document', '文件', '文件')}</span>
          </div>
          <div className="flex-1 min-h-0">
            <iframe
              src={`${WORKER_API_BASE}/file-storage/${item.id}/download?inline=1&token=${localStorage.getItem('token') || ''}${iframeClientParam()}`}
              className="w-full h-full border-0"
              title={tr('Document Preview', '文件預覽', '文件预览')}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
